import React, { useMemo } from 'react';
import { IResortPathInfo } from '@/api/resort/types';
import {
  CurrentLink, CurrentTab, linksPlan, linksSnow, onlyLodgingAndSkiRental,
} from '@/components/resort/ResortHeaderNav/constants';
import { isMainLocale } from '@/util/i18nFlags';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { useNoHeader } from '@/util/useNoHeader';
import { ResortHeader } from '@/components/resort/ResortHeader';
import type { SubNavItem } from '@/components/common/SubNav';
import { IRegionShort, IRelatedForRegion, IRelatedForResort } from '@/api/region/types';
import { RESORTS_WITH_SUMMER_GUIDE_PAGE } from '@/ots-constants';
import { Routes } from '@/routes';
import { getLocale } from '@/i18n/locale';
import { ELocale } from '@/i18n/localeEnum';

// Bridges the existing _layout API (which still passes photo / video /
// website / phone / related from the legacy HeroNav era) onto the v3
// ResortHeader. The link-building rules (linksSnow / linksPlan
// + locale gates + FR Steamboat exclusion) are unchanged; only the
// inner render swaps. The unused HeroNav-only props stay in the
// signature so _layout.tsx doesn't have to change in the same PR.
export const ResortHeaderNav = ({
  resort,
  link,
  liftsUrl,
  title,
  isPartner,
  website,
  phone,
  video,
  region,
}: {
  resort: IResortPathInfo,
  tab: CurrentTab,
  link: CurrentLink,
  liftsUrl?: string,
  photo: string,
  title: string,
  website?: string,
  phone?: string,
  isPartner?: boolean,
  video?: string,
  region?: IRegionShort,
  related?: IRelatedForRegion | IRelatedForResort,
  isOnSummerOverview?: boolean;
}) => {
  const { t } = useOTSTranslation('common');
  const locale = getLocale() as ELocale;

  const linksSnowPart = useMemo(() => {
    let links = linksSnow(resort, t, isPartner);

    if (!RESORTS_WITH_SUMMER_GUIDE_PAGE.some((item) => item.locale === locale && item.resortId === resort.uuid)) {
      links = links.filter((item) => item.uuid !== CurrentLink.SUMMER_OVERVIEW);
    }

    return links;
  }, [resort, t, isPartner]);

  const linksPlanningPart = useMemo(
    () => {
      let links = [...(isMainLocale() ? linksPlan(resort, liftsUrl, t) : onlyLodgingAndSkiRental(resort, t))];

      if (locale === ELocale.FR_FR && resort.uuid === '592') {
        links = links.filter((item) => item.uuid !== CurrentLink.BOOK_TRIP);
      }

      return links;
    }, [resort, liftsUrl, t, locale],
  );

  // Map both link groups into the v3 SubNav shape; mark the active
  // item so ResortHeader can paint the underline.
  const subNavItems: SubNavItem[] = useMemo(
    () => [...linksSnowPart, ...linksPlanningPart].map((l) => ({
      label: l.title,
      href: l.href,
      current: l.uuid === link,
      isExternal: l.isExternal,
    })),
    [linksSnowPart, linksPlanningPart, link],
  );

  // The current item's label doubles as the H1 page suffix

  // Region overview is the most useful single destination for both
  // breadcrumb crumbs as an MVP; we can break Country and Region into
  // separate links later.
  const breadcrumbHref = region ? Routes.regionSnowReport(region) : '#';

  const { noHeader } = useNoHeader();
  if (noHeader) {
    return null;
  }

  return (
    <ResortHeader
      country={region?.country ?? resort.country_code ?? ''}
      region={region?.title ?? resort.region_en ?? ''}
      resortName={resort.title}
      heading={title}
      subNav={subNavItems}
      breadcrumbHref={breadcrumbHref}
      sponsored={isPartner ? { website, phone, video } : undefined}
      className="mt-4"
    />
  );
};
